import React, { ChangeEvent, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { Button, Select, Space, Typography } from 'antd';
import * as Icons from '@ant-design/icons';
import { v1 as uuid } from 'uuid';
import _ from 'lodash';

import { StyledTableSummary } from '@shared/styled/table'
import { SelectField } from '@shared/modules/form/fields/select'
import { TextField } from '@shared/modules/form/fields/text'
import { ServiceCategory } from '../../services/types/categories'
import { useServiceCategoryList } from '../../services/hooks/use-category-list'

import { AppointmentFormData } from '../types';
import { EditableCell } from '../components/editable-cell';
import { ServicesTable } from '../components/services-table';
import { DiscountCalculator } from './discount-calculator';

const ServiceField = memo<{ index: number; serviceCategories: ServiceCategory[]; push: any; remove: any }>(
  ({ index, push, remove, serviceCategories }) => {
    const formik = useFormikContext<AppointmentFormData>()
    const { values, touched } = formik
    const fieldValues = useMemo(() => values.services[index], [index, values.services])
    const fieldTouched = useMemo(() => touched.services?.[index] || {}, [index, touched.services])

    const formikRef = useRef(formik)
    const fieldTouchedRef = useRef(fieldTouched)
    const indexRef = useRef(index)

    const services = useMemo<ServiceCategory['services']>(() => {
      return _.uniqBy(_.flatMap(serviceCategories, 'services'), 'id')
    }, [serviceCategories])

    const doctors = useMemo<ServiceCategory['services'][0]['doctors']>(() => {
      return services.find(({ id }) => id.toString() === fieldValues.id)?.doctors || []
    }, [fieldValues.id, services])

    const servicesNode = useMemo(() => {
      return serviceCategories.map((category) => {
        return (
          <Select.OptGroup key={category.id} label={category.name}>
            {category.services.map((service) => {
              return (
                <Select.Option
                  disabled={values.services.map(({ id }) => id).includes(service.id.toString())}
                  key={service.id}
                  value={service.id.toString()}
                >
                  {service.name}
                </Select.Option>
              )
            })}
          </Select.OptGroup>
        )
      })
    }, [serviceCategories, values.services])

    const doctorsNode = useMemo(
      () =>
        doctors.map((doctor) => (
          <Select.Option key={doctor.id} value={doctor.id.toString()}>
            {doctor.name}
          </Select.Option>
        )),
      [doctors],
    )

    const foundService = useMemo(() => services.find((service) => service.id.toString() === fieldValues.id), [fieldValues.id, services])
    const foundDoctor = useMemo(() => doctors.find((doctor) => doctor.id.toString() === fieldValues.doctorId), [doctors, fieldValues.doctorId])

    const rawServicePrice = useMemo(() => {
      if (foundService?.isAppointment) {
        return foundDoctor?.price || 0
      } else {
        return foundService?.price || 0
      }
    }, [foundDoctor?.price, foundService?.isAppointment, foundService?.price])

    const servicePrice = useMemo(() => {
      const quantity = parseInt(fieldValues.quantity || '1')

      if (!isNaN(parseFloat(fieldValues.customPrice))) {
        return parseFloat(fieldValues.customPrice) * quantity
      }

      if (foundService?.isAppointment) {
        return rawServicePrice * quantity
      } else {
        return rawServicePrice * quantity
      }
    }, [fieldValues.customPrice, fieldValues.quantity, foundService?.isAppointment, rawServicePrice])

    const price = useMemo(() => {
      const parsedDiscount = parseFloat(fieldValues.discount) || 0

      return servicePrice - parsedDiscount
    }, [fieldValues.discount, servicePrice])

    const handleRemove = useCallback(() => {
      remove(index)
    }, [index, remove])

    const handleAdd = useCallback(() => {
      push({ uuid: uuid(), id: '', doctorId: '', discount: '0', quantity: '1' })
    }, [push])

    const discountWritable = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(event.target.value) || 0
        return servicePrice - value >= 0
      },
      [servicePrice],
    )

    const handleCustomPriceChange = useCallback((value: string) => {
      formikRef.current.setFieldValue(`services.${indexRef.current}.customPrice`, value);
    }, []);

    const handleDiscountApply = useCallback((discountAmount: number) => {
      formikRef.current.setFieldValue(`services.${indexRef.current}.discount`, discountAmount.toString())
    }, [])

    useImperativeHandle(fieldTouchedRef, () => fieldTouched, [fieldTouched])
    useImperativeHandle(formikRef, () => formik, [formik])
    useImperativeHandle(indexRef, () => index, [index])

    useEffect(() => {
      if (!!fieldTouchedRef.current.id) {
        formikRef.current.setFieldValue(`services.${indexRef.current}.doctorId`, '')
        formikRef.current.setFieldValue(`services.${indexRef.current}.discount`, '0')
      }
    }, [fieldValues.id])

    useEffect(() => {
      if (!!fieldTouchedRef.current.doctorId) {
        formikRef.current.setFieldValue(`services.${indexRef.current}.customPrice`, '')
      }
    }, [fieldValues.doctorId])

    return (
      <tr>
        <td>
          <SelectField name={`services.${index}.id`}>{servicesNode}</SelectField>
        </td>
        <td>
          <SelectField name={`services.${index}.doctorId`} input={{ disabled: !fieldValues.id }}>
            {doctorsNode}
          </SelectField>
        </td>
        <td>
          <EditableCell editable={!!foundService ? { onChange: handleCustomPriceChange } : false}>
            {fieldValues.customPrice || rawServicePrice.toString() || '0'}
          </EditableCell>
        </td>
        <td>
          <Space size={8} align='start'>
            <DiscountCalculator
              servicePrice={servicePrice}
              onApply={handleDiscountApply}
              disabled={!fieldValues.id}
            />
            <div style={{ minWidth: "100px" }}>
              <TextField
                name={`services.${index}.discount`}
                format='integer'
                input={{ disabled: !fieldValues.id, suffix: '₼' }}
                writable={discountWritable}
              />
            </div>
          </Space>
        </td>
        <td>
          <TextField name={`services.${index}.quantity`} format='integer' input={{ disabled: !fieldValues.id }} />
        </td>
        <td>{price}</td>
        <td>
          {index === values.services.length - 1 ? (
            <Button onClick={handleAdd} type='dashed' icon={<Icons.PlusCircleOutlined />} />
          ) : (
            <Button onClick={handleRemove} type='dashed' danger={true} icon={<Icons.DeleteOutlined />} />
          )}
        </td>
      </tr>
    )
  },
)

const ServicesFieldArray: any = memo<FieldArrayRenderProps>(({ push, remove }) => {
  const { values, setFieldValue, errors } = useFormikContext<AppointmentFormData>()
  const serviceCategories = useServiceCategoryList()
  const services = useMemo(() => {
    return _.uniqBy(_.flatMap(serviceCategories.data, 'services'), 'id')
  }, [serviceCategories.data])
  const doctors = useMemo(() => {
    return _.uniqBy(_.flatMap(services, 'doctors'), 'id')
  }, [services])

  const totalPrice = useMemo(() => {
    const selectedServices = services
      .filter(({ id }) => values.services.map(({ id }) => id).includes(id.toString()))
      .map((service) => {
        const foundService = values.services.find(({ id }) => id === service.id.toString())
        const foundDoctor = doctors.find(({ id }) => id.toString() === foundService?.doctorId)
        const price = foundService?.customPrice || (service.isAppointment ? foundDoctor?.price || 0 : service.price)

        return { ...service, price, quantity: parseInt(foundService?.quantity || '1'), discount: parseFloat(foundService?.discount || '0') }
      })

    return selectedServices.reduce((acc, { price, quantity, discount }) => acc + (price * quantity - discount), 0)
  }, [services, doctors, values.services])

  useEffect(() => {
    setFieldValue('servicesTotalPrice', totalPrice)
  }, [setFieldValue, totalPrice])

  return (
    <ServicesTable>
      <thead>
        <tr>
          <th style={{ width: 'calc(50% - 200px)' }}>Xidmət</th>
          <th style={{ width: 'calc(50% - 200px)' }}>Həkim</th>
          <th style={{ width: 100 }}>X. Qiyməti</th>
          <th style={{ width: 100 }}>Endirim</th>
          <th style={{ width: 100 }}>Say</th>
          <th style={{ width: 100 }}>Qiymət</th>
          <th style={{ width: 1 }} />
        </tr>
      </thead>
      <tbody>
        {values.services.map((service, index) => (
          <ServiceField key={service.uuid} serviceCategories={serviceCategories.data} index={index} push={push} remove={remove} />
        ))}
        {typeof errors.services === 'string' && (
          <tr>
            <td colSpan={5}>
              <Typography.Text type='danger'>{errors.services}</Typography.Text>
            </td>
          </tr>
        )}
      </tbody>
      <tfoot>
        <tr>
          <StyledTableSummary colSpan={6}>Ümumi qiymət: {totalPrice}</StyledTableSummary>
        </tr>
      </tfoot>
    </ServicesTable>
  )
})

export const AppointmentServicesForm = memo(
  () => {
    return <FieldArray name='services' component={ServicesFieldArray} />
  },
  () => true,
)
